import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, Input, HostBinding, ContentChild, HostListener, NgModule } from '@angular/core';
import { Default } from '@rogers/cdk/common/decorators';
import * as i6 from '@rogers/cdk/icon';
import { IconComponent, IconModule } from '@rogers/cdk/icon';
import * as i1 from '@rogers/cdk/common/services';
import * as i2 from '@rogers/cdk/common/a11y';
import * as i3 from '@angular/cdk/platform';
import { PlatformModule } from '@angular/cdk/platform';
import * as i4 from '@angular/platform-browser';
import * as i5 from '@rogers/cdk/core';
import * as i7 from '@angular/common';
import { CommonModule } from '@angular/common';

class ButtonComponent {
    constructor(element, windowService, focusMonitor, platform, sanitizer, cdr, coreService) {
        this.element = element;
        this.windowService = windowService;
        this.focusMonitor = focusMonitor;
        this.platform = platform;
        this.sanitizer = sanitizer;
        this.cdr = cdr;
        this.coreService = coreService;
        this._hoverScaleUnit = 1;
        this.isAnchor = false;
        this._role = null;
        this.focusMonitor.monitor(this.element, true);
    }
    get buttonHoverScale() {
        return this.coreService.brand === 'rogers' ||
            this.coreService.brand === 'r4b'
            ? this.sanitizer.bypassSecurityTrustStyle(`scale(${this._hoverScaleUnit})`)
            : null;
    }
    ngOnInit() {
        this.applyDisabledAttributeState();
        if (this.windowService.isBrowser()) {
            this.isAnchor =
                this.element.nativeElement.nodeName.toLowerCase() ===
                    'a';
            this._role = this.isAnchor ? 'link' : 'button';
            if (!this.isIOS() && !this.isAndroid() && !this.isAnchor) {
                this.element.nativeElement.addEventListener('mouseenter', () => this.scaleUp());
                this.element.nativeElement.addEventListener('focus', () => this.scaleUp());
                this.element.nativeElement.addEventListener('mouseleave', () => this.scaleDown());
                this.element.nativeElement.addEventListener('blur', () => this.scaleDown());
            }
        }
    }
    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.element);
    }
    ngOnChanges() {
        this.applyDisabledAttributeState();
    }
    applyDisabledAttributeState() {
        this._disabledAttr =
            this.disabled === true || this.disabled === '' ? true : null;
        if (this._disabledAttr) {
            this.element.nativeElement.classList.remove('cdk-focused', 'cdk-keyboard-focused');
        }
    }
    scaleUp() {
        const width = this.element.nativeElement.clientWidth;
        this._hoverScaleUnit = ((width + 8) * 1) / width;
        this.cdr.markForCheck();
    }
    scaleDown() {
        this._hoverScaleUnit = 1;
        this.cdr.markForCheck();
    }
    onClick(e) {
        if (this.disabled) {
            e.preventDefault();
            e.stopPropagation();
        }
    }
    handleClick(e) {
        if (this.isAnchor &&
            (e.code === 'Space' ||
                e.key === 'Spacebar' ||
                e.code === 'Enter' ||
                e.key === 'Enter')) {
            e.preventDefault();
            this.element.nativeElement.click();
            e.stopPropagation();
        }
    }
    get nativeElement() {
        return this.element.nativeElement;
    }
    isIOS() {
        return this.platform.IOS;
    }
    isAndroid() {
        return this.platform.ANDROID;
    }
    showIcon() {
        return this.isAnchor && !this.iconRef && !this.img;
    }
}
ButtonComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonComponent, deps: [{ token: i0.ElementRef }, { token: i1.WindowService }, { token: i2.FocusMonitor }, { token: i3.Platform }, { token: i4.DomSanitizer }, { token: i0.ChangeDetectorRef }, { token: i5.CDKCoreService }], target: i0.ɵɵFactoryTarget.Component });
ButtonComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: ButtonComponent, selector: "button[ds-button], a[ds-button]", inputs: { variant: "variant", size: "size", alignIcon: "alignIcon", img: "img", transparent: "transparent", disabled: "disabled", tabIndex: "tabIndex", a11yDescription: "a11yDescription" }, host: { listeners: { "click": "onClick($event)", "keydown": "handleClick($event)" }, properties: { "class.ds-button": "true", "class.ds-corners": "true", "class.ds-pointer": "true", "class.text-center": "true", "class.mw-100": "true", "class.d-inline-block": "variant !== \"icon\"", "class.-primary": "variant === \"primary\"", "class.-primary-reversed": "variant === \"primary-reversed\"", "class.-secondary": "variant === \"secondary\"", "class.-secondary-ghost-light": "variant === \"secondary-ghost-light\"", "class.-secondary-ghost-dark": "variant === \"secondary-ghost-dark\"", "class.-tertiary": "variant === \"tertiary\"", "class.-image": "img", "class.-icon": "variant === \"icon\"", "class.d-inline-flex": "variant === \"icon\"", "class.justify-content-center": "variant === \"icon\"", "class.align-items-center": "variant === \"icon\"", "class.-large": "size === \"large\"", "class.-small": "size === \"small\"", "class.-transparent": "variant === \"icon\" && transparent === true", "class.-iconLeft": "alignIcon === \"left\"", "class.text-no-decoration": "true", "attr.disabled": "_disabledAttr", "attr.aria-disabled": "_disabledAttr", "attr.tabindex": "_disabledAttr ? -1 : tabIndex ||  0", "attr.role": "_role", "style.transform": "this.buttonHoverScale" } }, queries: [{ propertyName: "iconRef", first: true, predicate: IconComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<span\n  class=\"ds-button__wrapper d-flex justify-content-center align-items-center\"\n  role=\"text\"\n  [ngClass]=\"{ 'flex-row-reverse': alignIcon === 'left' }\"\n>\n  <img\n    *ngIf=\"img\"\n    [src]=\"img\"\n    class=\"mh-100 ds-button__image\"\n    height=\"48\"\n    width=\"200\"\n    alt=\"\"\n  />\n  <span\n    class=\"ds-button__copy text-button text-nowrap ds-no-overflow mw-100\"\n    [ngClass]=\"{ 'sr-only': variant === 'icon' || img }\"\n  >\n    <ng-content></ng-content>\n    <span *ngIf=\"a11yDescription\" class=\"sr-only\">{{ a11yDescription }}</span>\n  </span>\n  <ng-content *ngIf=\"!img\" select=\"ds-icon\"></ng-content>\n  <ds-icon *ngIf=\"showIcon()\" name=\"right\"></ds-icon>\n</span>\n", components: [{ type: i6.IconComponent, selector: "ds-icon", inputs: ["name", "color", "caption", "ariaHidden", "size"] }], directives: [{ type: i7.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i7.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], encapsulation: i0.ViewEncapsulation.None });
__decorate([
    Default('primary')
], ButtonComponent.prototype, "variant", void 0);
__decorate([
    Default('large')
], ButtonComponent.prototype, "size", void 0);
__decorate([
    Default('right')
], ButtonComponent.prototype, "alignIcon", void 0);
__decorate([
    Default(false)
], ButtonComponent.prototype, "transparent", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[ds-button], a[ds-button]', host: {
                        '[class.ds-button]': 'true',
                        '[class.ds-corners]': 'true',
                        '[class.ds-pointer]': 'true',
                        '[class.text-center]': 'true',
                        '[class.mw-100]': 'true',
                        '[class.d-inline-block]': 'variant !== "icon"',
                        '[class.-primary]': 'variant === "primary"',
                        '[class.-primary-reversed]': 'variant === "primary-reversed"',
                        '[class.-secondary]': 'variant === "secondary"',
                        '[class.-secondary-ghost-light]': 'variant === "secondary-ghost-light"',
                        '[class.-secondary-ghost-dark]': 'variant === "secondary-ghost-dark"',
                        '[class.-tertiary]': 'variant === "tertiary"',
                        '[class.-image]': 'img',
                        '[class.-icon]': 'variant === "icon"',
                        '[class.d-inline-flex]': 'variant === "icon"',
                        '[class.justify-content-center]': 'variant === "icon"',
                        '[class.align-items-center]': 'variant === "icon"',
                        '[class.-large]': 'size === "large"',
                        '[class.-small]': 'size === "small"',
                        '[class.-transparent]': 'variant === "icon" && transparent === true',
                        '[class.-iconLeft]': 'alignIcon === "left"',
                        '[class.text-no-decoration]': 'true',
                        '[attr.disabled]': '_disabledAttr',
                        '[attr.aria-disabled]': '_disabledAttr',
                        '[attr.tabindex]': '_disabledAttr ? -1 : tabIndex ||  0',
                        '[attr.role]': '_role',
                    }, encapsulation: ViewEncapsulation.None, template: "<span\n  class=\"ds-button__wrapper d-flex justify-content-center align-items-center\"\n  role=\"text\"\n  [ngClass]=\"{ 'flex-row-reverse': alignIcon === 'left' }\"\n>\n  <img\n    *ngIf=\"img\"\n    [src]=\"img\"\n    class=\"mh-100 ds-button__image\"\n    height=\"48\"\n    width=\"200\"\n    alt=\"\"\n  />\n  <span\n    class=\"ds-button__copy text-button text-nowrap ds-no-overflow mw-100\"\n    [ngClass]=\"{ 'sr-only': variant === 'icon' || img }\"\n  >\n    <ng-content></ng-content>\n    <span *ngIf=\"a11yDescription\" class=\"sr-only\">{{ a11yDescription }}</span>\n  </span>\n  <ng-content *ngIf=\"!img\" select=\"ds-icon\"></ng-content>\n  <ds-icon *ngIf=\"showIcon()\" name=\"right\"></ds-icon>\n</span>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.WindowService }, { type: i2.FocusMonitor }, { type: i3.Platform }, { type: i4.DomSanitizer }, { type: i0.ChangeDetectorRef }, { type: i5.CDKCoreService }]; }, propDecorators: { variant: [{
                type: Input
            }], size: [{
                type: Input
            }], alignIcon: [{
                type: Input
            }], img: [{
                type: Input
            }], transparent: [{
                type: Input
            }], disabled: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], a11yDescription: [{
                type: Input
            }], buttonHoverScale: [{
                type: HostBinding,
                args: ['style.transform']
            }], iconRef: [{
                type: ContentChild,
                args: [IconComponent, { static: false }]
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], handleClick: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

class ButtonModule {
}
ButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonModule, declarations: [ButtonComponent], imports: [CommonModule, PlatformModule, IconModule], exports: [ButtonComponent] });
ButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonModule, imports: [[CommonModule, PlatformModule, IconModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ButtonComponent],
                    imports: [CommonModule, PlatformModule, IconModule],
                    exports: [ButtonComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ButtonComponent, ButtonModule };
